import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [registrationDate, setRegistrationDate] = useState<string>('2025-04-23T00:00:00');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRegistrationDate();
  }, []);

  const fetchRegistrationDate = async () => {
    try {
      setIsLoading(true);

      const { data: tournament } = await supabase
        .from('tournaments')
        .select('registration_open_date')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (tournament?.registration_open_date) {
        setRegistrationDate(tournament.registration_open_date);
      }
    } catch (error) {
      console.error('Error fetching registration date:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const registrationDateTime = new Date(registrationDate);
      const now = new Date();
      const difference = registrationDateTime.getTime() - now.getTime();

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }

      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [registrationDate]);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="bg-black/30 backdrop-blur-sm rounded-xl p-6 mb-8 flex items-center justify-center"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent"></div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="bg-black/60 backdrop-blur-md rounded-xl p-4 sm:p-6 mb-8 border border-white/10"
    >
      <p className="text-white text-lg font-semibold mb-4 text-center">Registration Opens In:</p>
      <div className="grid grid-cols-4 gap-2 sm:gap-4">
        {[
          { label: 'Days', value: timeLeft.days },
          { label: 'Hours', value: timeLeft.hours },
          { label: 'Minutes', value: timeLeft.minutes },
          { label: 'Seconds', value: timeLeft.seconds }
        ].map((item) => (
          <div key={item.label} className="text-center bg-black/40 rounded-lg p-3">
            <div className="text-2xl sm:text-4xl font-display font-bold text-white mb-1">
              {String(item.value).padStart(2, '0')}
            </div>
            <div className="text-xs sm:text-sm text-golf-sand font-medium">{item.label}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}